// src/components/ITextToolbarSelected.js
import { useState, useEffect } from "react";
import { Shadow } from "fabric";
import { useData } from "../hooks/useData";
import { FontNames } from "./FontNames";
import useCanvasHistory from "../hooks/useCanvasHistory";
import {
  Box,
  Divider,
  Stack,
  FormGroup,
  TextField,
  InputAdornment,
  NativeSelect,
  FormControl,
  InputLabel,
  OutlinedInput,
  Slider,
  Checkbox,
  FormControlLabel,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";

const fontOptions = FontNames;

const ITextToolbarSelected = ({ canvas }) => {
  const { selectedObject, setSelectedObject, setSnackbar } = useData();
  const { saveHistory } = useCanvasHistory(canvas);

  // State for text properties
  const [canvasText, setCanvasText] = useState("");
  const [strokeFactor, setStrokeFactor] = useState(0); // Stroke as fraction of fontSize
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState("Arial");
  const [formats, setFormats] = useState([]);
  const [alignment, setAlignment] = useState("center");
  const [hasShadow, setHasShadow] = useState(false);
  const [shadowColor, setShadowColor] = useState("#343434");
  const [shadowBlur, setShadowBlur] = useState(5);
  const [shadowOffsetX, setShadowOffsetX] = useState(2);
  const [shadowOffsetY, setShadowOffsetY] = useState(2);

  // Initialize values when selectedObject changes
  useEffect(() => {
    if (selectedObject && selectedObject.type === "i-text") {
      setCanvasText(selectedObject.text || "");
      setFontSize(selectedObject.fontSize || 16);
      // Calculate strokeFactor from strokeWidth and fontSize
      const strokeWidth = selectedObject.strokeWidth || 0;
      const calculatedStrokeFactor =
        selectedObject.fontSize > 0 ? strokeWidth / selectedObject.fontSize : 0;
      setStrokeFactor(calculatedStrokeFactor);
      setFontFamily(selectedObject.fontFamily || "Arial");
      setAlignment(selectedObject.textAlign || "center");

      // Initialize formats (bold, italic)
      const currentFormats = [];
      if (selectedObject.fontWeight === "bold") currentFormats.push("bold");
      if (selectedObject.fontStyle === "italic") currentFormats.push("italic");
      setFormats(currentFormats);

      // Initialize shadow
      if (selectedObject.shadow && selectedObject.shadow.color) {
        setHasShadow(true);
        setShadowColor(selectedObject.shadow.color || "#343434");
        setShadowBlur(selectedObject.shadow.blur || 5);
        setShadowOffsetX(selectedObject.shadow.offsetX || 2);
        setShadowOffsetY(selectedObject.shadow.offsetY || 2);
      } else {
        setHasShadow(false);
      }

      // Ensure the object is selected on the canvas
      const activeObject = canvas.getActiveObject();
      if (activeObject !== selectedObject) {
        canvas.setActiveObject(selectedObject);
        canvas.requestRenderAll();
      }
      console.log("ITextToolbarSelected: Synced properties", {
        text: selectedObject.text,
        strokeWidth: selectedObject.strokeWidth,
        strokeFactor: calculatedStrokeFactor,
        fontSize: selectedObject.fontSize,
        fontFamily: selectedObject.fontFamily,
        textAlign: selectedObject.textAlign,
        fontWeight: selectedObject.fontWeight,
        fontStyle: selectedObject.fontStyle,
        shadow: selectedObject.shadow,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        backgroundColor: canvas.backgroundColor,
        viewportTransform: canvas.viewportTransform,
        zoom: canvas.getZoom(),
      });
    }
  }, [selectedObject, canvas]);

  // Validate that the selectedObject is still on the canvas
  const validateObject = () => {
    if (!selectedObject || selectedObject.type !== "i-text") {
      setSnackbar({
        open: true,
        message: "No text object selected.",
        severity: "error",
      });
      setSelectedObject(null);
      return false;
    }

    // Check if the object is still on the canvas
    const canvasObjects = canvas.getObjects();
    if (!canvasObjects.includes(selectedObject)) {
      setSnackbar({
        open: true,
        message: "The selected text object is no longer on the canvas.",
        severity: "error",
      });
      setSelectedObject(null);
      return false;
    }

    return true;
  };

  const handleTextChange = (e) => {
    if (!validateObject()) return;

    const textValue = e.target.value;
    selectedObject.set({ text: textValue });
    setCanvasText(textValue);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Text updated", {
      text: textValue,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleStrokeFactor = (e) => {
    if (!validateObject()) return;

    const strokeValue = Math.max(0, parseFloat(e.target.value) || 0);
    const strokeWidth = fontSize * strokeValue;
    selectedObject.set({
      strokeWidth: strokeWidth,
      stroke: strokeWidth > 0 ? "#000000" : null,
      strokeUniform: true,
    });
    setStrokeFactor(strokeValue);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Stroke factor updated", {
      strokeFactor: strokeValue,
      strokeWidth,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleFontSize = (e) => {
    if (!validateObject()) return;

    const fontSizeValue = parseInt(e.target.value, 10);
    if (fontSizeValue < 1) return;
    const strokeWidth = fontSizeValue * strokeFactor; // Recalculate strokeWidth
    selectedObject.set({
      fontSize: fontSizeValue,
      strokeWidth: strokeWidth,
      stroke: strokeWidth > 0 ? "#000000" : null,
    });
    setFontSize(fontSizeValue);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Font size updated", {
      fontSize: fontSizeValue,
      strokeWidth,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleFontFamily = (e) => {
    if (!validateObject()) return;

    const fontFamilyValue = e.target.value;
    selectedObject.set({ fontFamily: fontFamilyValue });
    setFontFamily(fontFamilyValue);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Font family updated", {
      fontFamily: fontFamilyValue,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleFontFormat = (e, newFormats) => {
    if (!validateObject()) return;

    selectedObject.set({ fontWeight: "normal", fontStyle: "normal" });
    newFormats.forEach((format) => {
      if (format === "bold") {
        selectedObject.set({ fontWeight: "bold" });
      } else if (format === "italic") {
        selectedObject.set({ fontStyle: "italic" });
      }
    });
    setFormats(newFormats);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Font format updated", {
      formats: newFormats,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleAlignment = (e, newAlignment) => {
    if (!newAlignment) return;
    if (!validateObject()) return;

    selectedObject.set({ textAlign: newAlignment });
    setAlignment(newAlignment);
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Alignment updated", {
      alignment: newAlignment,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });
  };

  const handleShadowState = (e) => {
    const newHasShadow = e.target.checked;
    setHasShadow(newHasShadow);
    handleTextShadow(newHasShadow);
  };

  const handleTextShadow = (newHasShadow = hasShadow) => {
    if (!validateObject()) return;

    if (newHasShadow) {
      selectedObject.set({
        shadow: new Shadow({
          color: shadowColor,
          offsetX: shadowOffsetX,
          offsetY: shadowOffsetY,
          blur: shadowBlur,
        }),
      });
    } else {
      selectedObject.set({ shadow: null });
    }
    setSelectedObject(selectedObject);
    canvas.requestRenderAll();
    canvas.fire("object:modified", { target: selectedObject });
    saveHistory(canvas);
    console.log("ITextToolbarSelected: Shadow updated", {
      hasShadow: newHasShadow,
      shadowColor,
      shadowBlur,
      shadowOffsetX,
      shadowOffsetY,
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      backgroundColor: canvas.backgroundColor,
    });

    // Log visibility
    const bounds = selectedObject.getBoundingRect();
    console.log("ITextToolbarSelected: Text visibility check", {
      text: selectedObject.text,
      left: selectedObject.left,
      top: selectedObject.top,
      bounds: {
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
      },
      isVisible:
        bounds.left >= 0 &&
        bounds.top >= 0 &&
        bounds.left + bounds.width <= canvas.getWidth() &&
        bounds.top + bounds.height <= canvas.getHeight(),
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      viewportTransform: canvas.viewportTransform,
      zoom: canvas.getZoom(),
    });
  };

  const handleShadowBlur = (e, newValue) => {
    setShadowBlur(newValue);
    handleTextShadow();
  };

  const handleShadowOffsetX = (e, newValue) => {
    setShadowOffsetX(newValue);
    handleTextShadow();
  };

  const handleShadowOffsetY = (e, newValue) => {
    setShadowOffsetY(newValue);
    handleTextShadow();
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Edit Text
      </Typography>
      <FormGroup>
        <TextField
          type="text"
          id="canvasText"
          margin="normal"
          InputProps={{ sx: { bgcolor: "#fff" } }}
          label="Text"
          value={canvasText}
          onChange={handleTextChange}
          variant="outlined"
        />
        <TextField
          type="number"
          id="strokeFactor"
          margin="normal"
          InputProps={{
            endAdornment: <InputAdornment position="end">x</InputAdornment>,
            sx: { bgcolor: "#fff", paddingLeft: 1 },
            inputProps: { min: 0, max: 0.2, step: 0.01 },
          }}
          label="Stroke Factor"
          value={strokeFactor}
          onChange={handleStrokeFactor}
          variant="outlined"
        />
        <TextField
          type="number"
          id="fontSize"
          margin="normal"
          InputProps={{
            endAdornment: <InputAdornment position="end">px</InputAdornment>,
            sx: { bgcolor: "#fff", paddingLeft: 1 },
            inputProps: { min: 1 },
          }}
          label="Size"
          value={fontSize}
          onChange={handleFontSize}
          variant="outlined"
        />
        <Divider
          sx={{
            borderBottomWidth: 1,
            borderColor: "grey",
            marginTop: "24px",
            marginBottom: "12px",
          }}
          variant="fullWidth"
          orientation="horizontal"
        />
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            marginTop: "20px",
            minWidth: "200px",
            marginBottom: "12px",
          }}
        >
          <InputLabel sx={{ color: "#5e5e5e" }} htmlFor="fontfamily">
            Font Style
          </InputLabel>
          <NativeSelect
            sx={{
              paddingLeft: 2,
              "& .MuiInputBase-input": { bgcolor: "#fff" },
            }}
            inputProps={{ id: "fontfamily" }}
            value={fontFamily}
            label="Font Style"
            input={<OutlinedInput label="Font Style" />}
            onChange={handleFontFamily}
          >
            {fontOptions.map((option) => (
              <option
                key={option}
                value={option}
                style={{ fontFamily: option }}
              >
                {option}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
        <Divider
          sx={{
            borderBottomWidth: 1,
            borderColor: "grey",
            marginTop: "24px",
            marginBottom: "12px",
          }}
          variant="fullWidth"
          orientation="horizontal"
        />
        <Box>
          <Stack direction="row" spacing={4} justifyContent="space-evenly">
            <ToggleButtonGroup
              value={formats}
              onChange={handleFontFormat}
              aria-label="text formatting"
            >
              <ToggleButton value="bold" aria-label="bold">
                <FormatBoldIcon />
              </ToggleButton>
              <ToggleButton value="italic" aria-label="italic">
                <FormatItalicIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={alignment}
              exclusive
              onChange={handleAlignment}
              aria-label="text alignment"
            >
              <ToggleButton
                value="left"
                aria-label="left aligned"
                size="medium"
              >
                <FormatAlignLeftIcon />
              </ToggleButton>
              <ToggleButton
                value="center"
                aria-label="center aligned"
                size="medium"
              >
                <FormatAlignCenterIcon />
              </ToggleButton>
              <ToggleButton
                value="right"
                aria-label="right aligned"
                size="medium"
              >
                <FormatAlignRightIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
        <Divider
          sx={{
            marginTop: "12px",
            marginBottom: "12px",
            borderWidth: 1,
            borderColor: "grey",
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
          }}
          variant="fullWidth"
          orientation="horizontal"
        />
        <FormControlLabel
          control={
            <Checkbox
              id="shadowState"
              checked={hasShadow}
              onChange={handleShadowState}
              inputProps={{ "aria-label": "controlled" }}
            />
          }
          label="Enable Shadow"
        />
        {hasShadow && (
          <Box bgcolor="white" mt={2} mb={2} p={2} width="100%">
            <Typography
              sx={{
                color: "#0d2445",
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 700,
                textAlign: "left",
              }}
            >
              Adjust Shadow
            </Typography>
            <Stack mb={2}>
              <Typography>Offset X</Typography>
              <Slider
                sx={{ width: "100%" }}
                aria-label="OffsetX"
                onChange={handleShadowOffsetX}
                value={shadowOffsetX}
                step={1}
                min={0}
                max={20}
                valueLabelDisplay="auto"
              />
            </Stack>
            <Stack mb={2}>
              <Typography>Offset Y</Typography>
              <Slider
                sx={{ width: "100%" }}
                aria-label="OffsetY"
                onChange={handleShadowOffsetY}
                value={shadowOffsetY}
                step={1}
                min={0}
                max={20}
                valueLabelDisplay="auto"
              />
            </Stack>
            <Stack>
              <Typography>Blur</Typography>
              <Slider
                sx={{ width: "100%" }}
                aria-label="Blur"
                value={shadowBlur}
                onChange={handleShadowBlur}
                step={1}
                min={0}
                max={20}
                valueLabelDisplay="auto"
              />
            </Stack>
          </Box>
        )}
      </FormGroup>
    </Box>
  );
};

export default ITextToolbarSelected;